import { isFuture, ISO8601, isPast } from '@nucleus/lib-datetime';
import { z } from 'zod';

type CustomOptions = Parameters<typeof z.custom>[1];

type RefineOptions = Parameters<z.ZodString['refine']>[1];

/**
 * Zod schema for color hex code strings
 *
 * Validates hex color codes with the following formats:
 * - 3 digits: #RGB (e.g., #F0A)
 * - 6 digits: #RRGGBB (e.g., #FF00AA)
 * - 8 digits: #RRGGBBAA (e.g., #FF00AA80) - includes alpha channel
 *
 * All formats must start with a pound sign (#)
 */
const hexColor = (options?: Exclude<CustomOptions, string> & { allowAlpha?: boolean }): z.ZodString => {
  // Default to true, but allow false.
  const allowAlpha = options?.allowAlpha ?? true;
  const lengths = [3, 6];
  if (allowAlpha) {
    lengths.push(8);
  }

  return z
    .string()
    .regex(/^#([0-9a-f]{3}){1,2}([0-9a-f]{2})?$/i, 'Invalid hex color format. Must be #RGB, #RRGGBB, or #RRGGBBAA')
    .refine((value) => lengths.includes(value.length - 1), {
      error: 'Invalid hex color format. Must be #RGB, #RRGGBB, or #RRGGBBAA',
      ...options,
    });
};

/**
 * TypeScript type inferred from the schema
 */

const iso8601 = (options?: Exclude<CustomOptions, string>): z.ZodCustom<ISO8601, string> =>
  z.custom<ISO8601, string>((value) => z.iso.datetime().safeParse(value).success, {
    error: 'Invalid ISO8601 date format',
    ...options,
  });

const iosIsPast = (options?: Exclude<RefineOptions, string>): z.ZodCustom<ISO8601, string> =>
  iso8601().refine((value) => isPast(value), {
    error: 'Date must be in the past',
    ...options,
  });

const isoIsFuture = (options?: Exclude<RefineOptions, string>): z.ZodCustom<ISO8601, string> =>
  iso8601().refine((value) => isFuture(value), {
    error: 'Date must be in the future',
    ...options,
  });

const slug = (options?: Exclude<RefineOptions, string>): z.ZodString =>
  z
    .string()
    .max(2048)
    .refine((value) => value === '' || /^[a-z\d]([a-z\d_-]*[a-z\d])*$/.test(value), {
      error:
        'Slug must start with a letter or digit and may only contain lowercase letters, digits, hyphens, and underscores.',
      ...options,
    });

const slugPath = (options?: Exclude<RefineOptions, string>): z.ZodString =>
  z
    .string()
    .max(2048)
    .refine((value) => value === '' || /^[a-z\d]+[a-z\d_-]*(\/[a-z\d_-]*[a-z\d])*$/.test(value), {
      error:
        'Slug must start with a letter or digit and may only contain lowercase letters, digits, hyphens, underscores, and forward slash.',
      ...options,
    });

export const range = (
  min: number,
  max: number,
  options?: Exclude<RefineOptions, string> & { name?: string }
): z.ZodNumber => {
  if (min > max) {
    throw new Error('Invalid range: min must be less than or equal to max');
  }

  return z
    .number()
    .min(min, {
      error: options?.error ?? `${options?.name ?? 'Value'} must be in the range of ${min} to ${max}`,
    })
    .max(max, {
      error: options?.error ?? `${options?.name ?? 'Value'} must be in the range of ${min} to ${max}`,
    });
};

export const zCustom = {
  hexColor: hexColor,
  isFuture: isoIsFuture,
  iso: iso8601,
  isPast: iosIsPast,
  range: range,
  slug: slug,
  slugPath: slugPath,
};
